import {Injectable, OnDestroy} from '@angular/core';
import {CompleteThemeDefinition, ThemingService as FundamentalThemingService} from '@fundamental-ngx/core';
import {debounceTime, distinctUntilChanged, map, Observable, Subscription} from 'rxjs';

export const DEFAULT_THEME = 'sap_horizon_dark';
export const SELECTED_THEME_KEY = 'selected-theme';

export interface ChartStyle {
  labelsColor: string,
  gridLinesColor: string
}

export const overrideTableColors =
  {
    '--sapLegendColor1': '#89D1FF',
    '--sapLegendColor2': '#B34B0F',
    '--sapLegendColor3': '#2CE0BF',
    '--sapLegendColor4': '#B26262',
    '--sapLegendColor5': '#FF8AF0',
    '--sapLegendColor6': '#7CA10C',
    '--sapLegendColor7': '#B894FF',
    '--sapLegendColor8': '#C88500',
    '--sapLegendColor9': '#FEACC8',
    '--sapLegendColor10': '#0A6ED1',
    '--sapLegendColor11': {
      'sap_horizon': '#0057D2',
      'sap_horizon_dark': '#1B90FF'
    },
    '--sapLegendColor12': '#F7AB5E',
    '--sapLegendColor13': '#07838F',
    '--sapLegendColor14': '#FFA1A1',
    '--sapLegendColor15': {
      'sap_horizon': '#A100C2',
      'sap_horizon_dark': '#F31DED'
    },
    '--sapLegendColor16': '#D4E45F',
    '--sapLegendColor17': '#5D36FF',
    '--sapLegendColor18': '#FFC654',
    '--sapLegendColor19': {
      'sap_horizon': '#BA066C',
      'sap_horizon_dark': '#FA4F96'
    },
    '--sapLegendColor20': '#A0CEFC'
  }

/**
 * Using a common theming service to avoid creating multiple inconsistent instance of Fundamental's ThemingService
 * Also provides facade for localStorage and initialization
 */
@Injectable({
  providedIn: 'root'
})
export class UniThemingService implements OnDestroy {
  private availableThemeIds = ['sap_horizon', 'sap_horizon_dark'];
  private _subs = new Subscription();

  constructor(private fundamentalThemingService: FundamentalThemingService) {
    this.setOverrideColorsByTheme();
  }

  currentTheme$(): Observable<CompleteThemeDefinition | null> {
    return this.fundamentalThemingService.currentTheme;
  }

  setTheme(themeId: string): boolean {
    localStorage.setItem(SELECTED_THEME_KEY, themeId);
    return this.fundamentalThemingService.setTheme(themeId);
  }

  getThemes(): CompleteThemeDefinition[] {
    return this.fundamentalThemingService.getThemes().filter(theme => this.availableThemeIds.includes(theme.id));
  }

  setInitialTheme(): boolean {
    const initialTheme = localStorage.getItem(SELECTED_THEME_KEY) ?? DEFAULT_THEME;
    return this.setTheme(initialTheme);
  }

  getChartComputedLabelsColor(): ChartStyle {
    const computedStyle = getComputedStyle(document.body);
    const labelsColor = computedStyle.getPropertyValue('--sapTextColor');
    const gridLinesColor = computedStyle.getPropertyValue('--sapToolbar_SeparatorColor');
    return {labelsColor, gridLinesColor}
  }

  getComputedColor$(color: string) : Observable<string> {
    return this.getComputedColors$([color]).pipe(map(colors => colors.pop()))
  }

  getComputedColors$(colors: string[]) : Observable<string[]> {
    return this.currentTheme$().pipe(
      distinctUntilChanged(),
      debounceTime(500),
      map((_theme) => {
        const computedColors = [];
        const computedStyle = getComputedStyle(document.body);
        for(const color of colors) {
          computedColors.push(computedStyle.getPropertyValue(color))
        }
        return computedColors;
      }))
  }

  setOverrideColorsByTheme() {
    this._subs.add(this.currentTheme$().pipe(distinctUntilChanged()).subscribe(currentTheme => {
      if (!currentTheme) {
        return;
      }
      this.setOverrideColors(currentTheme.id);
    }));
  }

  setOverrideColors(theme: string): void {
    const root: HTMLElement = document.querySelector(':root');
    Object.keys(overrideTableColors).forEach(colorVarName => {
      const colorValue = typeof overrideTableColors[colorVarName] === 'string' ? overrideTableColors[colorVarName] : overrideTableColors[colorVarName][theme];
      root.style.setProperty(colorVarName, colorValue);
    })
  }

  ngOnDestroy(): void {
    this._subs.unsubscribe();
  }
}
